import {Component, DestroyRef, OnInit, inject} from '@angular/core';
import {NgIf} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MenubarModule} from "primeng/menubar";
import {MenuItem} from "primeng/api";
import {AvatarModule} from "primeng/avatar";
import {BadgeModule} from "primeng/badge";
import {ImageModule} from "primeng/image";
import {MenuModule} from "primeng/menu";
import {ToastModule} from "primeng/toast";
import {AuthService} from "../../core/service/auth/Auth.service";
import {RoleEnum} from "../../core/modules/enums/roleEnum";
import {LocalePickerComponent} from "../../core/i18n/locale-picker/locale-picker.component";
import {NotificationBellComponent} from "../notification-bell/notification-bell.component";
import {LocaleService} from "../../core/i18n/locale.service";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {switchMap} from "rxjs";
import {Router} from "@angular/router";
import {LayoutService} from "../layout.service";

@Component({
  selector: 'app-nav',
  standalone: true,
  imports: [
    NgIf,
    MenubarModule,
    AvatarModule,
    BadgeModule,
    ImageModule,
    MenuModule,
    ToastModule,
    LocalePickerComponent,
    NotificationBellComponent,
    TranslatePipe,
    NotificationBellComponent,
    TranslatePipe
  ],
  templateUrl: './nav.component.html',
  styleUrl: './nav.component.css'
})
export class NavComponent implements OnInit {
  private readonly localeService = inject(LocaleService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutService = inject(LayoutService);

  /** Mobile-only hamburger: opens the sidenav as a slide-in drawer. */
  openSidenav(): void {
    this.layoutService.openSidenav();
  }
  menuItems: MenuItem[] = [];
  userName = '';


  constructor(private authService: AuthService, private router: Router) {
  }
  ngOnInit() {
    this.localeService.current$.pipe(
      switchMap(() => this.translate.get(['nav.profile', 'nav.settings', 'nav.logout'])),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(t => {
      // Only offer destinations this user can actually reach. This menu is shared by every
      // role through the single AppLayoutComponent shell, so listing Profile/Settings
      // unconditionally sent non-admin users (Customer Service, Salesman, …) straight to
      // Access Forbidden. Logout is the only entry everyone gets.
      const accountItems: MenuItem[] = [];
      if (this.canViewProfile) {
        accountItems.push({ label: t['nav.profile'], icon: 'pi pi-user', command: () => this.viewProfile() });
      }
      if (this.canOpenSettings) {
        accountItems.push({ label: t['nav.settings'], icon: 'pi pi-cog', command: () => this.openSettings() });
      }

      this.menuItems = [
        ...accountItems,
        // Skipped when nothing precedes it, so the menu never opens on a stray divider.
        ...(accountItems.length ? [{ separator: true } as MenuItem] : []),
        { label: t['nav.logout'], icon: 'pi pi-sign-out', command: () => this.logout() }
      ];
    });

    // Read from the login response cache (AuthService), not a fresh API call — the admin-only
    // /admin/profile endpoint 401s for Salesman/SalesManager, since this nav bar is shared by
    // every role via the single AppLayoutComponent shell.
    this.userName = this.authService.getCurrentUserName()?.trim() || '';
  }


  /**
   * Mirrors the roleGuard on /profile ([Admin, SubAdmin]). The page reads the admin-only
   * /admin/profile endpoint, so widening the route wouldn't help — a non-admin would just
   * trade Access Forbidden for a 401.
   */
  private get canViewProfile(): boolean {
    return this.authService.hasAnyRole([RoleEnum.Admin, RoleEnum.SubAdmin]);
  }

  /**
   * Mirrors the roleGuard on /project/settings — [Admin, SubAdmin] narrowed by the 'project'
   * permission group for SubAdmins. Every other user type sees no Settings entry.
   */
  private get canOpenSettings(): boolean {
    const role = this.authService.getRole();
    if (role === RoleEnum.Admin) return true;
    return role === RoleEnum.SubAdmin && this.authService.hasPermissionGroup('project');
  }

  viewProfile() {
    this.router.navigate(['/profile']);
  }

  openSettings() {
    this.router.navigate(['/project/settings']);
  }

  logout() {
    console.log('Logging out...');
    this.authService.logout()

    // Add your logout logic here
  }


}
